// 4-step welcome modal — reordered so the hackathon HOOK fires
// immediately after the welcome (step 2), not at the end. We show a
// live mini-leaderboard preview so the prize feels real before the
// curriculum gets explained.
//
// Steps:
//   1. Welcome      — what HackerLabs is, in one sentence.
//   2. The hook     — live pot + top-3 leaderboard preview, "you?" row.
//   3. The arc      — 100 skills, agent unlock at #25.
//   4. The promise  — 5 min/skill, no signup, exit anytime, claim CTA.
import { useState } from "react";
import {
  Sparkles,
  Compass,
  Bot,
  Rocket,
  ArrowRight,
  Check,
  Trophy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useHackathon, formatUsd } from "@/hooks/useHackathon";

interface Props {
  open: boolean;
  onClose: () => void;
  onFinish: () => void;
}

const STEPS = [
  {
    icon: Sparkles,
    title: "Welcome to HackerLabs.",
    body: "You're about to learn AI the way hackers do — by using it. Real tasks, in real chats, every step.",
  },
  {
    icon: Trophy,
    title: "There's a pot. You can win it.",
    body: "We run an always-on hackathon with a real cash prize. The minute you finish onboarding you're eligible. The secret: newbies win it more often than veterans.",
  },
  {
    icon: Compass,
    title: "100 skills, 5 tiers.",
    body: "Awareness → Application → Leverage → Systems → Architecture. At skill #25 you build your first agent — that's where it gets fun.",
  },
  {
    icon: Rocket,
    title: "5 minutes per skill.",
    body: "We show you what to do. You try it. You mark it done. Next one unlocks. No sign-in needed. Exit anytime.",
  },
];

const WelcomeOnboardingModal = ({ open, onClose, onFinish }: Props) => {
  const [step, setStep] = useState(0);
  const { pot, scoreboard } = useHackathon({ scoreboardLimit: 3 });
  const potLabel = pot ? formatUsd(pot.pot_cents) : "$10,427";
  const top3 = scoreboard.slice(0, 3);
  const isLast = step === STEPS.length - 1;
  const Step = STEPS[step];
  const Icon = Step.icon;

  const handleNext = () => {
    if (isLast) {
      onFinish();
      setStep(0);
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleSkip = () => {
    onFinish();
    setStep(0);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          setStep(0);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-primary/15 grid place-items-center">
            <Icon className="w-7 h-7 text-primary" />
          </div>
          <DialogTitle className="text-center font-mono text-xl">
            {Step.title}
          </DialogTitle>
          <DialogDescription className="text-center font-mono text-xs pt-2 leading-relaxed">
            {Step.body}
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex items-center justify-center gap-1.5 my-3"
          role="tablist"
          aria-label="Welcome progress"
        >
          {STEPS.map((_, i) => (
            <span
              key={i}
              role="tab"
              aria-selected={i === step}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-6 bg-primary" : "w-1.5 bg-secondary"
              }`}
            />
          ))}
        </div>

        {/* Step 2 — hackathon tease with live pot + mini-leaderboard */}
        {step === 1 && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 my-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Current pot
              </span>
              <span className="text-base font-mono font-bold text-primary tabular-nums">
                {potLabel}
              </span>
            </div>
            {top3.length > 0 && (
              <div className="space-y-1 pt-1 border-t border-primary/20">
                {top3.map((row, i) => (
                  <div
                    key={row.user_id}
                    className="flex items-center gap-2 text-[11px] font-mono"
                  >
                    <span className="w-4 text-muted-foreground">#{i + 1}</span>
                    <span className="flex-1 truncate text-foreground">
                      @{row.handle}
                    </span>
                    <span className="text-primary tabular-nums">
                      {row.score.toLocaleString()} pts
                    </span>
                  </div>
                ))}
                <div className="flex items-center gap-2 text-[11px] font-mono pt-1 border-t border-primary/20 text-muted-foreground">
                  <span className="w-4">#?</span>
                  <span className="flex-1 italic blur-[1px]">you?</span>
                  <span className="italic">— start playing</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — agent unlock visual hint */}
        {step === 2 && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 my-2 flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary shrink-0" />
            <p className="text-[11px] font-mono text-foreground">
              <span className="text-primary font-bold">Skill #25</span> = your
              first agent. That's the moment you stop consuming AI and start
              building with it.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 mt-2">
          <button
            type="button"
            onClick={handleNext}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {isLast ? (
              <>
                <Check className="w-4 h-4" />
                Claim my spot — start skill #1
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={handleSkip}
              className="w-full text-[11px] font-mono text-muted-foreground hover:text-foreground"
            >
              Skip the tour
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeOnboardingModal;
